import {
    Box,
    FormControlLabel,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    styled,
} from '@mui/material'
import { Icon } from 'packages/eid-icons'
import { TextInput } from 'packages/eid-ui'
import { useEffect, useState } from 'react'
import { ReactComponent as GroupIconSvg } from '../../eid-ui/RadioList/Resources/group.svg'
import { ReactComponent as OvalIconSvg } from '../../eid-ui/RadioList/Resources/oval.svg'
import MultiSelectDropdown from './Components/MultiSelectDropdown'

const ns = 'public'

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
    marginLeft: '0px',
    marginRight: '20px',
    fontSize: '1.2rem',
    color: '#767676',
    height: '3rem',
    '&:focus-within': {
        color: '#000000',
    },
}))

const StyledSelect = styled(Select)(({ theme }) => ({
    width: '100%',
    boxShadow: '0 0.4rem 0.6rem -0.2rem rgba(0, 0, 0, 0.1)',
    padding: '0.8rem 1.2rem 0.8rem 1.6rem',
    border: 'solid 0.1rem #d8d8dd',
    borderRadius: '0.5rem',
    '& .MuiSelect-select:focus': {
        backgroundColor: 'white',
    },
    '&:before': {
        content: 'none !important',
    },
}))

const StyledBox = styled(Box)(({ theme }) => ({
    marginTop: '2rem',
    '& label': {
        paddingLeft: '0 !important',
        position: 'relative',
        top: '0.5rem',
    },
    '& input': {
        boxShadow: '0 0.4rem 0.6rem -0.2rem rgba(0, 0, 0, 0.1)',
        padding: '0.8rem 1.2rem 0.8rem 1.6rem',
        border: 'solid 0.1rem #d8d8dd',
        borderRadius: '0.5rem',
    },
}))

const StyledRadioBox = styled(Box)(({ theme }) => ({
    marginTop: '2rem',
    '& label': {
        textTransform: 'uppercase',
        color: '#b4b4b4',
        marginBottom: '1rem',
        fontSize: '1.2rem !important',
        fontWeight: '300 !important',
    },
}))

const StyledFormLabel = styled(FormLabel)(({ theme }) => ({
    marginBottom: '2rem !important',
    height: 'auto !important',
}))

const DynamicForm = ({
    formFieldJson,
    onFieldChange,
    showErrorMessages,
    t,
}) => {
    const [formData, setFormData] = useState(null)

    useEffect(() => {
        if (
            formFieldJson &&
            formFieldJson.mappingFields &&
            formFieldJson.mappingFields.length > 0 &&
            !formData
        ) {
            prepareFormDataFromJson()
        }
    }, [formFieldJson, formData])

    const bindRequiredColor = (fieldName) => {
        if (formData) {
            const mappedField = formData.find((x) => x.name === fieldName)
            if (mappedField) {
                return mappedField.isRequired && !mappedField.value
            }
        } else {
            return false
        }
    }

    const prepareFormDataFromJson = () => {
        let formFileds = []
        formFieldJson.mappingFields.forEach((item) => {
            formFileds.push({
                name: item.fieldName,
                value: '',
                isRequired: item.required ? item.required : false,
                localeKey: item.localeKey,
            })
        })
        setFormData(formFileds)
    }

    const bindFields = () => {
        return formFieldJson.mappingFields.map((item, index) => {
            if (item.fieldType === 'text') {
                return (
                    <StyledBox
                        sx={{
                            '& label': {
                                color: bindRequiredColor(item.fieldName)
                                    ? 'red !important'
                                    : 'inherit',
                            },
                        }}
                    >
                        <TextInput
                            key={index + item.fieldName}
                            validationError={checkIfRequired(item.fieldName)}
                            value={bindValue(item.fieldName)}
                            name={item.fieldName}
                            handleChange={handleInputChange}
                            label={
                                (item.localeKey
                                    ? t(
                                          `${ns}:MsCommonAnonymous_${item.localeKey}`,
                                      )
                                    : item.fieldName) +
                                (item.required ? ' *' : '')
                            }
                        />{' '}
                    </StyledBox>
                )
            }
            if (item.fieldType === 'dropdown') {
                return bindDropDown(item)
            }
            if (item.fieldType === 'radio') {
                return bindRadioList(item)
            }
            if (item.fieldType === 'multiselect') {
                return (
                    <MultiSelectDropdown
                        bindValue={bindValue}
                        handleDDChange={handleDDChange}
                        t={t}
                        item={item}
                        ns={ns}
                        bindRequiredColor={(itemName) =>
                            bindRequiredColor(itemName)
                        }
                    />
                )
            }
            return <></>
        })
    }

    const checkIfRequired = (fieldName) => {
        if (showErrorMessages && formData) {
            const mappedField = formData.find((x) => x.name === fieldName)
            if (mappedField) {
                return mappedField.isRequired && !mappedField.value
            }
        } else {
            return false
        }
    }

    const bindValue = (fieldName) => {
        if (formData) {
            const mappedField = formData.find((x) => x.name === fieldName)
            if (mappedField) {
                return mappedField.value
            }
        }
    }

    const updateFormDataOnChange = (data) => {
        let mappedFormData = [...formData]
        const findChangedMappingField = mappedFormData.findIndex(
            (x) => x.name === data.name,
        )
        if (findChangedMappingField >= 0) {
            mappedFormData[findChangedMappingField].value = data.value
            setFormData(mappedFormData)
        }
        onFieldChange(mappedFormData)
    }

    const handleInputChange = (evt, fieldName) => {
        if (evt && evt.currentTarget) {
            const changeData = {
                name: fieldName,
                value: evt.currentTarget.value,
            }
            updateFormDataOnChange(changeData)
        }
    }

    const handleradioChange = (evt) => {
        if (evt && evt.currentTarget) {
            const changeData = {
                name: evt.currentTarget.name,
                value: evt.currentTarget.value,
            }
            updateFormDataOnChange(changeData)
        }
    }

    const handleDDChange = (evt) => {
        if (evt && evt.target) {
            const changeData = {
                name: evt.target.name,
                value: evt.target.value,
            }
            updateFormDataOnChange(changeData)
        }
    }

    const StyledRadio = styled(Radio)({
        '&.Mui-checked': {
            '& .MuiSvgIcon-root': {
                display: 'none',
            },
            '& + .MuiFormControlLabel-label': {
                '&::before': {
                    content: '""',
                    display: 'inline-block',
                    width: '1.6rem',
                    height: '1.6rem',
                    backgroundImage: `url(${GroupIconSvg})`,
                    backgroundSize: 'contain',
                    marginRight: '0.8rem',
                    verticalAlign: 'middle',
                },
            },
        },
        '& + .MuiFormControlLabel-label': {
            '&::before': {
                content: '""',
                display: 'inline-block',
                width: '1.6rem',
                height: '1.6rem',
                backgroundImage: `url(${OvalIconSvg})`,
                backgroundSize: 'contain',
                marginRight: '0.8rem',
                verticalAlign: 'middle',
            },
        },
    })

    const bindRadioList = (item) => {
        return (
            <StyledRadioBox>
                <StyledFormLabel
                    component="label"
                    sx={{
                        color: bindRequiredColor(item.fieldName)
                            ? 'red !important'
                            : 'inherit',
                    }}
                >
                    {(item.localeKey
                        ? t(`${ns}:MsCommonAnonymous_${item.localeKey}`)
                        : item.fieldName) + (item.required ? ' *' : '')}
                </StyledFormLabel>
                <Box marginTop="1rem">
                    <RadioGroup
                        defaultValue="100"
                        aria-label="100"
                        name={item.fieldName}
                        value={'value'}
                        onChange={(e) => handleradioChange(e)}
                    >
                        {item.options.map((option, index) => (
                            <StyledFormControlLabel
                                key={option.value + index}
                                value={option.value}
                                control={<StyledRadio />}
                                label={
                                    option.localeKey
                                        ? t(
                                              `${ns}:MsCommonAnonymous_${option.localeKey}`,
                                          )
                                        : option.value
                                }
                                checked={
                                    bindValue(item.fieldName) === option.value
                                        ? true
                                        : false
                                }
                            />
                        ))}
                    </RadioGroup>
                </Box>
            </StyledRadioBox>
        )
    }

    const bindDropDown = (item) => {
        return (
            <Box marginTop="1rem" sx={{ marginTop: '2rem !important' }}>
                <InputLabel
                    sx={{
                        textTransform: 'uppercase',
                        color: bindRequiredColor(item.fieldName)
                            ? 'red !important'
                            : '#b4b4b4',
                        marginBottom: '0.5rem',
                    }}
                    id={`dynamic-form-dropdown-${item.fieldName}`}
                >
                    {(item.localeKey
                        ? t(`${ns}:MsCommonAnonymous_${item.localeKey}`)
                        : item.fieldName) + (item.required ? ' *' : '')}
                </InputLabel>
                <StyledSelect
                    labelId={`dynamic-form-dropdown-${item.fieldName}`}
                    id={`dynamic-form-dropdown-options-${item.fieldName}`}
                    value={bindValue(item.fieldName)}
                    onChange={(e) => handleDDChange(e)}
                    label={
                        item.localeKey
                            ? t(`${ns}:MsCommonAnonymous_${item.localeKey}`)
                            : item.fieldName
                    }
                    name={item.fieldName}
                >
                    {item.options.map((option, index) => (
                        <MenuItem key={`option-${index}`} value={option.value}>
                            {option.localeKey
                                ? t(
                                      `${ns}:MsCommonAnonymous_${option.localeKey}`,
                                  )
                                : option.value}
                        </MenuItem>
                    ))}
                </StyledSelect>
            </Box>
        )
    }

    return (
        <Box
            width="100%"
            display="flex"
            zIndex={1}
            boxShadow="0px 0.2rem 1.6rem 0px rgba(0, 0, 0, 0.07)"
            borderRadius="1.6rem"
            padding="1rem 3.1rem 2rem 2rem"
        >
            <Box marginTop="3rem" display="flex" marginRight="2rem">
                <Icon
                    name="dynamicForm"
                    height="12rem"
                    width="12rem"
                    color="#D2D2D9"
                />
            </Box>
            <Box width="50%">
                {formFieldJson &&
                    formFieldJson.mappingFields &&
                    formFieldJson.mappingFields.length > 0 &&
                    bindFields()}
            </Box>
        </Box>
    )
}

export default DynamicForm
